<template>
    <div class="automation-block-editor run-javascript">
        <div class="editor-row customize-editor-minimize">
            <div class="editor-row-title">JavaScript Kodu</div>
            <div class="code-editor-wrapper">
                <div class="code-editor-header">
                    <div class="tips">
                        <div class="tip">
                            <md-icon>info</md-icon>
                            <span>Bu blok, kullanıcının yazdığı JavaScript kodunu çalıştırır.</span>
                        </div>
                        <div class="tip">
                            <md-icon>warning</md-icon>
                            <span>Güvenlik nedenlerinden dolayı bazı JavaScript özellikleri kısıtlanmıştır.</span>
                        </div>
                    </div>
                    <div class="actions">
                        <a-button type="primary" class="ai-assistant-btn" @click="showPromptWizard">
                            <img src="/static/img/copilot.png" width="15" height="15" style="margin-right: 5px" />
                            Supsis Asistan
                        </a-button>
                    </div>
                </div>

                <div class="code-editor">
                    <div class="available-libraries">
                        <span>Kullanılabilir Kütüphaneler:</span>
                        <code>moment</code>, <code>lodash (_)</code>, <code>axios (sadece get ve post)</code>
                    </div>

                    <div class="available-variables">
                        <span>Kullanılabilir Değişkenler:</span>
                        <code>params</code> (önceki blokların değerleri),
                        <code>task.readAttribute(key, defaultValue)</code>,
                        <code>task.bindAttribute(data)</code>
                    </div>

                    <div class="attribute-help">
                        <span>Otomasyon Değişkenleri:</span>
                        <code>block.blockId</code>, <code>event.eventType.eventName</code>, <code>system.date</code>,
                        <code>webhook.body.fieldName</code> şeklinde kullanılabilir.
                    </div>

                    <div id="monaco-editor-container" class="monaco-editor-container"></div>
                </div>

                <div class="code-restrictions">
                    <div class="restriction-title">Kısıtlamalar:</div>
                    <ul>
                        <li>process.exit, child_process gibi tehlikeli Node.js özellikleri yasaktır</li>
                        <li>require(), eval() ve Function() fonksiyonları kullanılamaz</li>
                        <li>Dosya sistemi (fs), kripto ve ağ işlemleri kısıtlanmıştır</li>
                        <li>Kod çalışma süresi 5 saniye ile sınırlıdır</li>
                        <li>HTTP istekleri için zaman aşımı 3 saniyedir</li>
                        <li>Sonsuz döngüler ve serverlar oluşturulamaz</li>
                    </ul>
                </div>

                <div class="example-usage">
                    <div class="example-title">Kod Örneği:</div>
                    <pre>
async function main(params) {
  // Moment kütüphanesini kullanma örneği
  const now = moment().format('YYYY-MM-DD HH:mm:ss');

  // Lodash kullanımı
  const users = [
    { name: 'Mesut', age: 30 },
    { name: 'Ahmet', age: 25 }
  ];
  const oldest = _.maxBy(users, 'age');

  // Otomasyon değişkenlerini kullanma
  const date = task.readAttribute('system.date');
  const contactName = task.readAttribute('contact.fullname');

  // Supsis SDK kullanım örneği
  let result = {};

  try {
    // Kullanıcıları listeleme
    const userList = await supsis.users.list();
    result.userCount = userList.length;

    // Kişi bilgisi alma (varsa)
    if (params.contactId) {
      const contact = await supsis.contact.get(params.contactId);
      result.contactInfo = {
        name: contact.fullname,
        email: contact.email
      };
    }

    // Modüller üzerinde çalışma
    // 1. Tüm modülleri listele
    const modules = await supsis.module.list();
    result.moduleCount = modules.length;

    // 2. Özel bir modül ile çalışma (örnek: müşteriler modülü)
    // const newCustomer = await supsis.module.customers.create({
    //   fullname: contactName,
    //   registerDate: now,
    //   status: 'active'
    // });
    // result.newCustomerId = newCustomer._id;

    // 3. Modül kaydı arama
    // const customerRecords = await supsis.module.customers.search({
    //   filters: [{ field: 'status', operator: 'eq', value: 'active' }],
    //   sort: { fullname: 1 },
    //   cursor: { page: 1, perPage: 10 }
    // });
    // result.customerCount = customerRecords.total;
  } catch (error) {
    // Hata yönetimi
    result.error = error.message;
  }

  // Döndürülen değer, bloğun çıktısı olarak kaydedilir
  return {
    currentTime: now,
    oldestUser: oldest.name,
    calculated: params.previousValue ? params.previousValue * 2 : 0,
    systemDate: date,
    contactName: contactName,
    apiResults: result
  };
}
</pre
                    >
                </div>
            </div>
        </div>

        <!-- JavaScript Asistan Modal -->
        <a-modal
            v-model="showJsAssistant"
            title="JavaScript Asistanı"
            :width="800"
            :footer="null"
            @cancel="handleAssistantClose"
        >
            <div class="js-assistant-modal">
                <div class="js-assistant-description">
                    <h4>JavaScript kodunu yapay zeka ile oluşturun</h4>
                    <p>
                        Yapmak istediğiniz işlemi doğal dilde açıklayın, Supsis Asistan sizin için JavaScript kodunu
                        oluştursun.
                    </p>

                    <div class="js-capabilities">
                        <div class="capabilities-header">
                            <a-icon type="bulb" />
                            <h5>Yapabilecekleriniz:</h5>
                        </div>
                        <div class="capabilities-content">
                            <div class="capability-item">
                                <i class="material-icons">functions</i>
                                <span>Hesaplamalar yapma</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">compare_arrows</i>
                                <span>Veri dönüşümleri</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">filter_list</i>
                                <span>Veri filtreleme</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">http</i>
                                <span>API istekleri yapma</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">today</i>
                                <span>Tarih işlemleri</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">format_list_numbered</i>
                                <span>Dizi işlemleri</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="prompt-container">
                    <s2-textarea
                        v-model="jsPrompt"
                        :rows="4"
                        :placeholder="'Örnek: Bir müşterinin toplam harcamasını hesapla ve eğer 1000 TL üzerindeyse VIP olarak etiketle'"
                        ref="promptTextarea"
                    />

                    <div class="example-prompts">
                        <div
                            class="example-prompt"
                            @click="
                                selectExamplePrompt(
                                    'Bir müşterinin toplam harcamasını hesapla ve eğer 1000 TL üzerindeyse VIP olarak etiketle'
                                )
                            "
                        >
                            <a-icon type="calculator" />
                            <span>Harcama hesapla ve etiketle</span>
                        </div>
                        <div
                            class="example-prompt"
                            @click="
                                selectExamplePrompt(
                                    'Müşteri adını ve soyadını alarak tam ad oluştur ve selamlama mesajı hazırla'
                                )
                            "
                        >
                            <a-icon type="user" />
                            <span>Müşteri bilgisiyle mesaj hazırla</span>
                        </div>
                        <div
                            class="example-prompt"
                            @click="
                                selectExamplePrompt(
                                    'API\'den hava durumu bilgisi al ve sıcaklığa göre aktivite önerisi yap'
                                )
                            "
                        >
                            <a-icon type="cloud" />
                            <span>API ile hava durumuna göre öneri</span>
                        </div>
                    </div>

                    <div class="buttons-container">
                        <a-button @click="handleAssistantClose">İptal</a-button>
                        <a-button type="primary" :loading="assistantLoading" @click="generateJavaScriptCode">
                            Kod Oluştur
                        </a-button>
                    </div>
                </div>
            </div>
        </a-modal>
    </div>
</template>

<script>
import _ from "lodash";
import * as monaco from "monaco-editor";
import AutomationAttributesGenerator from "../../../utils/AutomationAttributesGenerator";

import CoPilot from "../../../../../../utils/CoPilot";
import S2Textarea from "../../../../../../components/S2Textarea.vue";
import { createCompletionItemProvider } from "../createCompletionItemProvider";
import { useSupsisCompletion } from "../composables/useSupsisCompletion";

export default {
    name: "RunJavaScript",
    components: {
        S2Textarea,
    },
    props: ["value", "blockId", "automation"],
    setup() {
        // Supsis SDK otomatik tamamlama işlevini kur
        const { registerEditor } = useSupsisCompletion({
            // Gerekli konfigürasyon seçenekleri ile başlatılıyor
        });

        return {
            registerEditor,
        };
    },
    data() {
        return {
            payload: _.cloneDeep(this.value) || {
                code: "",
            },
            cancels: [],
            editor: null,
            attributesLoaded: false,
            autocompleteInitialized: false,
            automationAttributes: [],
            showJsAssistant: false,
            jsPrompt: "",
            assistantLoading: false,
            examplePrompts: [
                "Bir müşterinin toplam harcamasını hesapla ve eğer 1000 TL üzerindeyse VIP olarak etiketle",
                "Müşteri adını ve soyadını alarak tam ad oluştur ve selamlama mesajı hazırla",
                "API'den hava durumu bilgisi al ve sıcaklığa göre aktivite önerisi yap",
                "Geçmiş siparişlerin ortalamasını hesapla ve gelecek tahmini yap",
                "Müşteri adresinden il ve ilçe bilgisini ayıkla ve kargo firmasını belirle",
            ],
            moduleCache: [],
        };
    },
    async created() {
        // Supsis store modüllerini sync et (otomatik tamamlama için)
        try {
            await this.$store.dispatch('module/syncModules');
        } catch (error) {
            console.error('Modülleri sync ederken hata oluştu:', error);
        }
        // Modülleri yükle (Monaco editor'de kullanılacak)
        try {
            if (this.$store && this.$store.state.module && this.$store.state.module.modules) {
                this.moduleCache = this.$store.state.module.modules;
            } else {
                const response = await this.$_api.table.getModuleList();
                if (response.data && response.data.data) {
                    this.moduleCache = response.data.data;
                }
            }
        } catch (error) {
            console.error("Modül listesi yüklenirken hata oluştu:", error);
        }
    },
    mounted() {
        this.initMonaco();
        this.loadAutomationAttributes();
        window.addEventListener("resize", this.handleResize);
    },
    beforeDestroy() {
        this.destroyMonaco();
        window.removeEventListener("resize", this.handleResize);
    },
    methods: {
        getFlatAttributes(attributes) {
            const flat = (attr, parentLabel = "") => {
                const label = parentLabel ? `${parentLabel} > ${attr.label}` : attr.label;
                attr.fullLabel = label;

                if (attr.subAttributes) {
                    return [attr, ...attr.subAttributes.flatMap((x) => flat(x, attr.fullLabel))];
                }
                return [attr];
            };
            return attributes.flatMap((x) => flat(x, undefined));
        },
        loadAutomationAttributes() {
            const automation = this.automation || this.$store.state.automation.current;
            if (automation) {
                let attributes = AutomationAttributesGenerator.create().list(automation);
                attributes = attributes.filter((x) => !["conversation", "contact"].includes(x.context));
                this.automationAttributes = attributes;
                this.flatAutomationAttributes = this.getFlatAttributes(attributes);
                this.attributesLoaded = true;

                // Editör mevcutsa önceki sağlayıcıları temizleyip yeni provider ekle
                if (this.editor) {
                    this.cancels.forEach((disposeFn) => disposeFn());
                    this.cancels = [];
                    const disposable = monaco.languages.registerCompletionItemProvider(
                        "javascript",
                        createCompletionItemProvider(this.automationAttributes, this.moduleCache)
                    );
                    this.cancels.push(() => disposable.dispose());
                }

                console.log("Yüklenen attribute sayısı:", attributes.length);
                if (attributes.length > 0) {
                    console.log("Örnek attribute:", attributes[0]);
                }
            }
        },
        initMonaco() {
            // Monaco'da browser global type'larını ve önerilerini kaldır
            monaco.languages.typescript.javascriptDefaults.setExtraLibs([]);
            monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
                target: monaco.languages.typescript.ScriptTarget.ES6,
                module: monaco.languages.typescript.ModuleKind.CommonJS,
                allowNonTsExtensions: true,
                noLib: true, // Varsayılan kütüphaneleri (örn: lib.dom.d.ts) yükleme
            });
            // monaco-editor başlat
            this.editor = monaco.editor.create(document.getElementById("monaco-editor-container"), {
                value: this.payload.code,
                language: "javascript",
                theme: "vs-dark",
                minimap: {
                    enabled: false,
                },
                automaticLayout: true,
                scrollBeyondLastLine: false,
                lineNumbers: "on",
                roundedSelection: true,
                cursorStyle: "line",
                wordWrap: "on",
                tabSize: 2,
                fontSize: 14,
                suggestOnTriggerCharacters: true,
                quickSuggestions: {
                    other: true,
                    comments: false,
                    strings: true,
                },
                acceptSuggestionOnCommitCharacter: true,
                acceptSuggestionOnEnter: "on",
                inlineSuggest: {
                    enabled: true,
                },
            });

            // Düzenlemeleri payload'a ilet
            this.editor.onDidChangeModelContent(() => {
                this.payload.code = this.editor.getValue();
                this.$emit("input", this.payload);
            });

            // Trigger karaketerlerini ayarla
            this.editor.getModel().updateOptions({
                tabSize: 2,
                insertSpaces: true,
                trimAutoWhitespace: true,
                semanticHighlighting: {
                    enabled: true,
                },
            });

            // Otomatik öneriler için trigger karakterleri ekle
            monaco.languages.setLanguageConfiguration("javascript", {
                wordPattern: /(-?\d*\.\d\w*)|([^\`\~\!\@\#\$\%\^\&\*\(\)\=\+\[\{\]\}\\\|\;\:\'\"\,\.\<\>\/\?\s]+)/g,
                comments: {
                    lineComment: "//",
                    blockComment: ["/*", "*/"],
                },
                brackets: [
                    ["{", "}"],
                    ["[", "]"],
                    ["(", ")"],
                ],
                autoClosingPairs: [
                    { open: "{", close: "}" },
                    { open: "[", close: "]" },
                    { open: "(", close: ")" },
                    { open: '"', close: '"' },
                    { open: "'", close: "'" },
                    { open: "`", close: "`" },
                ],
                surroundingPairs: [
                    { open: "{", close: "}" },
                    { open: "[", close: "]" },
                    { open: "(", close: ")" },
                    { open: '"', close: '"' },
                    { open: "'", close: "'" },
                    { open: "`", close: "`" },
                ],
                folding: {
                    markers: {
                        start: /^\s*\/\/\s*#?region\b/,
                        end: /^\s*\/\/\s*#?endregion\b/,
                    },
                },
                onEnterRules: [
                    {
                        beforeText: /^\s*(?:\/\*|\/\/.*)?$/,
                        action: { indentAction: monaco.languages.IndentAction.None, appendText: "// " },
                    },
                ],
            });

            // Otomatik tamamlama sağlayıcısı ekle (her zaman güncel)
            this.cancels.forEach((disposeFn) => disposeFn());
            this.cancels = [];
            const disposable = monaco.languages.registerCompletionItemProvider(
                "javascript",
                createCompletionItemProvider(this.automationAttributes, this.moduleCache)
            );
            this.cancels.push(() => disposable.dispose());
        },
        destroyMonaco() {
            if (this.editor) {
                this.editor.dispose();
            }
        },
        handleResize() {
            if (this.editor) {
                this.editor.layout();
            }
        },
        showPromptWizard() {
            this.showJsAssistant = true;
            this.jsPrompt = "";
            // Modal açıldığında prompt alanına focus
            this.$nextTick(() => {
                if (this.$refs.promptTextarea) {
                    this.$refs.promptTextarea.focus();
                }
            });
        },
        handleAssistantClose() {
            this.showJsAssistant = false;
        },
        selectExamplePrompt(prompt) {
            this.jsPrompt = prompt;
        },
        async generateJavaScriptCode() {
            if (!this.jsPrompt.trim()) {
                this.$message.warning("Lütfen bir istek yazın");
                return;
            }

            try {
                this.assistantLoading = true;

                // CoPilot servisini kullanarak AI'dan kod oluşturma
                const copilot = CoPilot.getInstance();

                // Mevcut otomasyon bilgilerini ve prompt'u AI'a gönderelim
                const response = await copilot.generateJavaScriptCode({
                    siteId: this.$store.state.site.site._id,
                    customerPrompt: this.jsPrompt,
                    automationAttributes: this.flatAutomationAttributes.map((x) => ({
                        label: x.fullLabel,
                        value: x.value,
                        valueType: x.valueType,
                    })),
                    blockId: this.blockId,
                    automationId: this.automation?._id || this.$store.state.automation.current?._id,
                });

                if (response && response.code) {
                    console.log("response", response);
                    // Kodu editöre yerleştir
                    this.editor.setValue(response.code);
                    this.payload.code = response.code;
                    this.$emit("input", this.payload);

                    // Modal'ı kapat
                    this.showJsAssistant = false;
                    this.$message.success("JavaScript kodu başarıyla oluşturuldu");
                } else {
                    this.$message.error("Kod oluşturulurken bir hata oluştu");
                }
            } catch (error) {
                console.error("Kod üretme hatası:", error);
                this.$message.error("Kod oluşturulurken bir hata oluştu: " + (error.message || "Bilinmeyen hata"));
            } finally {
                this.assistantLoading = false;
            }
        },
        onEditorMounted(editor) {
            this.editor = editor;

            // Otomatik tamamlama için Register et
            this.registerEditor(editor, this.automationAttributes);

            // Supsis SDK şu an kullanılabilir
            editor.getModel().setValue(this.generatedCode || this.value);
        },
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        moduleCache: {
            handler(newCache) {
                if (this.editor && newCache) {
                    console.log("Module cache changed, re-registering completion provider with cache:", JSON.parse(JSON.stringify(newCache)));
                    this.cancels.forEach((disposeFn) => disposeFn());
                    this.cancels = [];
                    const disposable = monaco.languages.registerCompletionItemProvider(
                        "javascript",
                        createCompletionItemProvider(this.automationAttributes, newCache) 
                    );
                    this.cancels.push(() => disposable.dispose());
                }
            },
            deep: true,
        },
        "value.code": {
            handler(newValue) {
                // Dış değişiklikler olduğunda editörü güncelle (eğer editör ve model varsa)
                if (this.editor && this.editor.getModel() && newValue !== this.editor.getValue()) {
                    this.editor.setValue(newValue || "");
                }
            },
        },
        automation: {
            deep: true,
            handler() {
                this.loadAutomationAttributes();
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-block-editor.run-javascript {
    padding: 20px;
    display: flex;
    gap: 20px;
    flex-direction: column;

    .editor-row {
        width: 100%;

        &.customize-editor-minimize {
            display: flex;
            flex-direction: column;
            gap: 5px;
        }

        .editor-row-title {
            font-weight: bold;
            font-size: 16px;
            margin-bottom: 10px;
        }
    }

    .code-editor-wrapper {
        background-color: #f7f7f7;
        border-radius: 8px;
        padding: 15px;
        margin-bottom: 20px;

        .code-editor-header {
            margin-bottom: 15px;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;

            .tips {
                display: flex;
                flex-direction: column;
                gap: 10px;

                .tip {
                    // display: flex;
                    // align-items: center;
                    font-size: 14px;
                    color: #666;

                    md-icon {
                        margin-right: 8px;
                        font-size: 18px;
                    }
                }
            }

            .actions {
                .ai-assistant-btn {
                    display: flex;
                    align-items: center;
                    background-color: #3a86ff;
                    color: white;
                    border: none;
                }
            }
        }

        .code-editor {
            margin-bottom: 20px;

            .available-libraries,
            .available-variables,
            .attribute-help {
                margin-bottom: 10px;
                font-size: 14px;

                code {
                    background-color: #e9e9e9;
                    padding: 2px 4px;
                    border-radius: 3px;
                    font-size: 12px;
                }
            }

            .attribute-help {
                background-color: #e3f1ff;
                border-radius: 5px;
                padding: 8px;
                margin-top: 10px;
                border-left: 3px solid #3a86ff;
            }

            .monaco-editor-container {
                width: 100%;
                height: 600px;
                border-radius: 5px;
                overflow: hidden;
                margin-top: 10px;
            }
        }

        .code-restrictions {
            margin-bottom: 20px;
            background-color: #fff0f0;
            padding: 10px 15px;
            border-radius: 5px;
            border-left: 3px solid #ff6b6b;

            .restriction-title {
                font-weight: bold;
                margin-bottom: 10px;
                color: #ff4757;
            }

            ul {
                margin: 0;
                padding-left: 20px;

                li {
                    font-size: 13px;
                    margin-bottom: 5px;
                    color: #555;
                }
            }
        }

        .example-usage {
            background-color: #f0f8ff;
            padding: 15px;
            border-radius: 5px;
            border-left: 3px solid #5352ed;

            .example-title {
                font-weight: bold;
                margin-bottom: 10px;
                color: #3742fa;
            }

            pre {
                background-color: #f8f9fa;
                padding: 15px;
                border-radius: 5px;
                overflow-x: auto;
                font-size: 13px;
                line-height: 1.5;
                color: #333;
                margin: 0;
            }
        }
    }
}

/* JavaScript Asistan Modal Stilleri */
.js-assistant-modal {
    display: flex;
    flex-direction: column;
    gap: 20px;

    .js-assistant-description {
        h4 {
            margin-bottom: 10px;
        }

        p {
            margin-bottom: 20px;
            color: #666;
        }

        .js-capabilities {
            background-color: #f8f9fa;
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 20px;
            border: 1px dashed #d9d9d9;

            .capabilities-header {
                display: flex;
                align-items: center;
                margin-bottom: 10px;

                h5 {
                    margin: 0;
                    margin-left: 8px;
                    font-weight: 600;
                    font-size: 14px;
                }

                .anticon {
                    color: #1890ff;
                }
            }

            .capabilities-content {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 10px;

                .capability-item {
                    display: flex;
                    align-items: center;

                    .material-icons {
                        margin-right: 8px;
                        color: #1890ff;
                        font-size: 16px;
                    }

                    span {
                        font-size: 13px;
                    }
                }
            }
        }
    }

    .prompt-container {
        display: flex;
        flex-direction: column;
        gap: 15px;

        .example-prompts {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;

            .example-prompt {
                flex: 1;
                min-width: 200px;
                background-color: #f0f7ff;
                border: 1px solid #d6e4ff;
                border-radius: 6px;
                padding: 10px;
                cursor: pointer;
                display: flex;
                align-items: center;

                &:hover {
                    background-color: #d6e4ff;
                }

                .anticon {
                    margin-right: 8px;
                    color: #1890ff;
                }

                span {
                    font-size: 13px;
                }
            }
        }

        .buttons-container {
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            margin-top: 10px;
        }
    }
}
</style>
<style lang="scss">
.editor-widget {
    width: 100% !important;
}
</style>
